import React, { useState } from 'react';
import './Onboarding.css';

interface OnboardingProps {
  onComplete: (preferences?: any) => void;
}

export default function Onboarding({ onComplete }: OnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    interests: [] as string[],
    experience: '',
    goals: [] as string[],
    frequency: '',
    emailNotifications: false
  });

  const questions = [
    {
      id: 'interests',
      title: 'What topics interest you most?',
      subtitle: 'Select all that apply',
      type: 'multi-select',
      options: [
        'Healthcare & Medical',
        'Education & Schools',
        'Environment & Climate',
        'Economy & Jobs',
        'Civil Rights & Equality',
        'Immigration',
        'Gun Rights & Safety',
        'Veterans Affairs',
        'Technology & Privacy',
        'Infrastructure & Transportation'
      ]
    },
    {
      id: 'experience',
      title: 'How familiar are you with government and politics?',
      subtitle: 'This helps us personalize your experience',
      type: 'single-select',
      options: [
        'Very familiar - I follow politics closely',
        'Somewhat familiar - I keep up with major news',
        'Not very familiar - I\'m just getting started',
        'Complete beginner - I want to learn more'
      ]
    },
    {
      id: 'goals',
      title: 'What do you hope to get from this app?',
      subtitle: 'Select all that apply',
      type: 'multi-select',
      options: [
        'Stay informed about important legislation',
        'Understand how bills affect my community',
        'Make my voice heard through voting and comments',
        'Learn about the legislative process',
        'Track specific issues I care about',
        'Connect with others who share my concerns'
      ]
    },
    {
      id: 'frequency',
      title: 'How often do you plan to use this app?',
      subtitle: 'This helps us show you the most relevant content',
      type: 'single-select',
      options: [
        'Daily - I want to stay very engaged',
        'Weekly - I\'ll check in regularly',
        'Monthly - I\'ll browse occasionally',
        'As needed - When important issues arise'
      ]
    },
    {
      id: 'emailNotifications',
      title: 'Email Notifications',
      subtitle: 'Stay updated on bills you care about',
      type: 'email-preference',
      options: []
    }
  ];

  const handleMultiSelect = (questionId: string, option: string) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId as keyof typeof prev] as string[];
      const newAnswers = currentAnswers.includes(option)
        ? currentAnswers.filter(item => item !== option)
        : [...currentAnswers, option];
      
      return {
        ...prev,
        [questionId]: newAnswers
      };
    });
  };

  const handleSingleSelect = (questionId: string, option: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleEmailPreferenceToggle = () => {
    setAnswers(prev => ({
      ...prev,
      emailNotifications: !prev.emailNotifications
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save preferences and complete onboarding
      localStorage.setItem('userPreferences', JSON.stringify(answers));
      onComplete(answers);
    }
  };

  const handleSkip = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Save preferences and complete onboarding
      localStorage.setItem('userPreferences', JSON.stringify(answers));
      onComplete(answers);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    const currentQuestion = questions[currentStep];
    const currentAnswer = answers[currentQuestion.id as keyof typeof answers];
    
    if (currentQuestion.type === 'multi-select') {
      return (currentAnswer as string[]).length > 0;
    } else if (currentQuestion.type === 'email-preference') {
      return true; // Email preference is optional
    } else {
      return currentAnswer !== '';
    }
  };

  const currentQuestion = questions[currentStep];

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
        
        <div className="question-section">
          <div className="question-header">
            <h2>{currentQuestion.title}</h2>
            <p className="question-subtitle">{currentQuestion.subtitle}</p>
          </div>
          
          <div className="options-container">
            {currentQuestion.type === 'email-preference' ? (
              <div className="email-preference-container">
                <div className="email-preference-card">
                  <div className="email-preference-header">
                    <h3>📧 Get Email Updates</h3>
                    <p>Receive notifications when bills you're tracking have status changes</p>
                  </div>
                  <div className="email-preference-options">
                    <label className="email-option">
                      <input
                        type="checkbox"
                        checked={answers.emailNotifications}
                        onChange={handleEmailPreferenceToggle}
                      />
                      <span className="checkmark"></span>
                      <div className="option-content">
                        <strong>Yes, send me email notifications</strong>
                        <p>I want to stay informed about bill updates and important legislative changes</p>
                      </div>
                    </label>
                    <label className="email-option">
                      <input
                        type="checkbox"
                        checked={!answers.emailNotifications}
                        onChange={() => setAnswers(prev => ({ ...prev, emailNotifications: false }))}
                      />
                      <span className="checkmark"></span>
                      <div className="option-content">
                        <strong>No, I'll check the app manually</strong>
                        <p>I prefer to check for updates when I visit the app</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              currentQuestion.options.map((option, index) => {
                const isSelected = currentQuestion.type === 'multi-select'
                  ? (answers[currentQuestion.id as keyof typeof answers] as string[]).includes(option)
                  : answers[currentQuestion.id as keyof typeof answers] === option;
                
                return (
                  <button
                    key={index}
                    className={`option-button ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      if (currentQuestion.type === 'multi-select') {
                        handleMultiSelect(currentQuestion.id, option);
                      } else {
                        handleSingleSelect(currentQuestion.id, option);
                      }
                    }}
                  >
                    <span className="option-text">{option}</span>
                    {currentQuestion.type === 'multi-select' && (
                      <span className="checkbox">
                        {isSelected ? '✓' : ''}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
        
        <div className="navigation-buttons">
          <div className="left-buttons">
            {currentStep > 0 && (
              <button onClick={handleBack} className="back-btn">
                ← Back
              </button>
            )}
            <button 
              onClick={handleSkip} 
              className="skip-btn"
            >
              Skip
            </button>
          </div>
          <button 
            onClick={handleNext} 
            className={`next-btn ${canProceed() ? 'active' : 'disabled'}`}
            disabled={!canProceed()}
          >
            {currentStep === questions.length - 1 ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
